import confetti from "canvas-confetti";
import { feedback } from "../services/feedback";

const PERFECT_THRESHOLD = 4500;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function sparkleBurst(durationMs: number): void {
  // Fire sparkles every ~80ms across the burst window.
  const start = performance.now();
  const tick = () => {
    if (performance.now() - start >= durationMs) return;
    feedback.confettiSparkle();
    window.setTimeout(tick, 60 + Math.random() * 50);
  };
  tick();
}

export function celebrateScore(roundScore: number): void {
  if (roundScore < PERFECT_THRESHOLD) {
    return;
  }
  if (prefersReducedMotion()) {
    return;
  }

  const intensity = Math.min(1, (roundScore - PERFECT_THRESHOLD) / (5000 - PERFECT_THRESHOLD) + 0.4);
  const count = Math.round(180 * intensity) + 60;

  confetti({
    particleCount: count,
    spread: 80,
    origin: { y: 0.5, x: 0.5 },
    startVelocity: 38,
    ticks: 220,
    gravity: 0.9,
    scalar: 0.95,
    colors: ["#7dd3fc", "#fbbf24", "#fb923c", "#8fd694", "#f8fafc"]
  });
  sparkleBurst(700);

  if (intensity >= 0.9) {
    void window.setTimeout(() => {
      confetti({
        particleCount: Math.round(80 * intensity),
        spread: 100,
        origin: { y: 0.6, x: 0.18 },
        angle: 60,
        scalar: 0.85
      });
      confetti({
        particleCount: Math.round(80 * intensity),
        spread: 100,
        origin: { y: 0.6, x: 0.82 },
        angle: 120,
        scalar: 0.85
      });
      sparkleBurst(550);
    }, 220);
  }
}

/**
 * Animate a numeric counter from `from` to `to` over `durationMs`.
 * Returns a cancel function.
 */
export function animateNumber(
  element: HTMLElement,
  from: number,
  to: number,
  durationMs: number,
  format: (value: number) => string = (value) => Math.round(value).toString()
): () => void {
  if (prefersReducedMotion()) {
    element.textContent = format(to);
    return () => undefined;
  }
  let raf = 0;
  let cancelled = false;
  const start = performance.now();
  const tick = (now: number) => {
    if (cancelled) return;
    const t = Math.min(1, (now - start) / durationMs);
    const eased = easeOutCubic(t);
    const value = from + (to - from) * eased;
    element.textContent = format(value);
    if (t < 1) {
      raf = window.requestAnimationFrame(tick);
    }
  };
  raf = window.requestAnimationFrame(tick);
  return () => {
    cancelled = true;
    cancelAnimationFrame(raf);
  };
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
